import React, { useMemo, useState, useCallback } from 'react';
import {
  DataGridPro,
  Toolbar,
} from '@mui/x-data-grid-pro';
import {
  GridToolbarColumnsButton,
  GridToolbarExportButton,
  GridToolbarFilterButton,
} from '../utils/gridToolbarParts.js';
import { withSelectAllAwareCheckbox } from '../utils/gridSelectAllCheckbox.js';
import { CustomQuickFilter } from '../utils/CustomQuickFilter.js';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { usePersistedDataStore } from '../../store';
import { fields } from '../../../common/useStreamProcessorNew';
import BackToAnalysisButton from '../BackToAnalysisButton';
import MarketingInputs from './MarketingInputs';
import MarketingTablePollingComponent from './MarketingTablePollingComponent';


// idk if we care about enforcing this minimum revenue amount.
const minimumRecentSaleRevenue = 100;
const ROWS_PER_PAGE = 100; // Adjust based on your needs
const CHUNK_SIZE = 1000;

const CustomToolbar = ({ MuniCode, isFetching, totalCases, currentCount, marketingResponse, minimumThreshold, highTaxCutoff }) => {
  return (
    <Toolbar sx={{ display: 'flex', alignItems: 'center', padding: '0.5rem', justifyContent: 'space-between' }}>
      <div>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExportButton options={{ fileName: `${MuniCode}_Marketing` }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <>
            <Typography variant="body2">
              Calculated {currentCount || 0} out of {totalCases || 'loading...'}
            </Typography>
            {isFetching && (
              <CircularProgress size={16} />
            )}
          </>
      </div>
      <div>
        <CustomQuickFilter />
      </div>
    </Toolbar>
  );
};

export default function MarketingTable({MuniCode, countyString, muniString, TaxYear}) {
  // logger.log('🎨 MarketingTable: Component render', {
  //   timestamp: new Date().toISOString()
  // });
  
  // Use local state that only updates when polling detects actual changes
  const [localMarketingResponse, setLocalMarketingResponse] = useState([]);
  const [localTotalCases, setLocalTotalCases] = useState(null);
  const [localIsFetching, setLocalIsFetching] = useState(false);
  
  // Still need to read from store for polling component
  const getIsFetching = usePersistedDataStore((state) => state.isFetching);
  
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [minimumThreshold, setMinimumThreshold] = useState(0);
  const [highTaxCutoff, setHighTaxCutoff] = useState(30000);
  
  // Callback for polling component to update local state only when data changes
  const handlePollingUpdate = useCallback(({ marketingResponse, totalCases, isFetching }) => {
    // logger.log('🔄 MarketingTable: handlePollingUpdate called', {
    //   dataLength: marketingResponse?.length || 0,
    //   totalCases,
    //   isFetching
    // });
    setLocalMarketingResponse(marketingResponse || []);
    setLocalTotalCases(totalCases);
    setLocalIsFetching(isFetching);
  }, []);

  const columns = useMemo(() => {
    return fields.map(({ field, headerName, width, type }) => ({
      field,
      headerName,
      width: width || 150,
      valueFormatter: type === 'numeric'
        ? (value) => {
            if (!value && value !== 0) return '';
            if (isNaN(Number(value))) return value;
            return Math.round(Number(value)).toLocaleString();
          }
        : type === 'percent'
        ? (value) => {
            if (!value && value !== 0) return '';
            if (isNaN(Number(value))) return value;
            return `${Number(value*100).toFixed(2)}%`;
          }
        : undefined,
    }));
  }, []);

  useMemo(() => {
    if (Object.keys(columnVisibilityModel).length === 0) {
      setColumnVisibilityModel(
        fields.reduce((acc, { field }) => {
          acc[field] = true;
          return acc;
        }, {})
      );
    }
  }, [fields, columnVisibilityModel]);

  // Sort the processed rows by ProjectedAventineRevenue
  // TEMPORARILY DISABLED - sorting is off
  const sortedRows = useMemo(() => {
    // Temporarily disabled sorting - return original order
    // return [...(localMarketingResponse ?? [])].sort((a, b) =>
    //   (b.ProjectedAventineRevenue || 0) - (a.ProjectedAventineRevenue || 0)
    // );
    return localMarketingResponse ?? [];
  }, [localMarketingResponse]);

  const areaTitle = muniString !== "All" ? muniString : countyString;

  return (
    <div className="h-full flex flex-col ">
      {/* Polling component - isolates polling logic to prevent unnecessary rerenders */}
      <MarketingTablePollingComponent 
        onDataUpdate={handlePollingUpdate}
        isFetching={getIsFetching}
      />

      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        className="w-full"
      >
        <Box display="flex" width="100%" flexDirection="column" alignItems="center" my={!localIsFetching ? 2 : 0}>
          <Box 
            // set this to be max width xl
            display="flex" 
            width="100%" 
            alignItems="center" 
            justifyContent="space-between"
            sx={{ 
              minHeight: localIsFetching ? '64px' : 'auto',
              '& .MuiButtonBase-root': {
                alignSelf: 'center'
              }
            }}
          >
            <BackToAnalysisButton />
            <Typography variant="h4" sx={{textAlign: 'center' }}>
              {areaTitle} {TaxYear} Marketing
            </Typography>
            <Box sx={{ width: { xs: '40px', sm: '200px' } }} /> 
          </Box>
        </Box>

        <MarketingInputs 
          marketingResponse={localMarketingResponse}
          title={areaTitle} 
          minimumThreshold={minimumThreshold}
          setMinimumThreshold={setMinimumThreshold}
          minimumRecentSaleRevenue={minimumRecentSaleRevenue}
          highTaxCutoff={highTaxCutoff}
          setHighTaxCutoff={setHighTaxCutoff}
        />
      </Box>

      {/* Data Grid */}
      <DataGridPro
        rows={sortedRows}
        columns={columns}
        rowBuffer={5}
        rowHeight={52}
        style={{ 
          border: '1px solid rgba(224, 224, 224, 1)',
          height: '70vh'
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        checkboxSelection
        loading={!localMarketingResponse || localMarketingResponse.length === 0}
        disableRowSelectionOnClick
        getRowId={(row) => row.id || row.parcel_id}
        keepNonExistentRowsSelected
        slots={withSelectAllAwareCheckbox({
          toolbar: () => (
            <CustomToolbar 
              MuniCode={MuniCode} 
              isFetching={localIsFetching}
              totalCases={localTotalCases}
              currentCount={localMarketingResponse?.length}
              marketingResponse={localMarketingResponse}
              minimumThreshold={minimumThreshold}
              highTaxCutoff={highTaxCutoff}
            />
          )
        })}
        showToolbar />
    </div>
  );
}
